// ============================================================
// attack/swingNotes - the lines a SWING prints before its outcome:
// talents, combat-action toggles, and an auto-hitting template's shape.
// Pure; asserted verbatim - never reword a line.
//
// `hasWS` comes through the engine BARREL for the hook tests' mocks (see
// `shotNotes`).
// ============================================================
import { hasWS } from '../../engine/index';
import { AVOID_TEST_LABEL, sprayLengthOf, type ResolvedArea } from '../../engine/areaShapes';
import type { Slot } from '../../types';

/** What the attacker's talents and toggles did to this swing - the flags `swingNotes` reads. */
export interface SwingFlags {
  isMelee: boolean;
  rangeBand: string;
  removeLongRangePenalty: boolean;
  removeExtremeRangePenalty: boolean;
  isCalledShot: boolean;
  calledShotReduction: number;
  /** The penalty is waived by the Controls switch, not by a talent. */
  calledShotIgnored?: boolean;
  isAllOutAttack: boolean;
  isGuardedAttack: boolean;
  isCharge: boolean;
  hasBerserkCharge: boolean;
  hasHammerBlow: boolean;
  isGangUp: boolean;
  frenzied: boolean;
}

/** Called Shot reduced by Precise Blow (melee) or Deadeye Shot (ranged), or ignored outright. */
function calledShotNote(f: SwingFlags): string | null {
  if (!f.isCalledShot || f.calledShotReduction <= 0) return null;
  if (f.calledShotIgnored) return 'Called Shot: penalty ignored.';
  const effectivePenalty = Math.max(20 - f.calledShotReduction, 0);
  const talentName = f.isMelee ? 'Precise Blow' : 'Deadeye Shot';
  return talentName + ': Called Shot penalty reduced to -' + effectivePenalty + '.';
}

type SwingRule = (f: SwingFlags) => string | null;

// Declarative: talent lines (range, called shot), then the combat-action toggles, in log order.
const SWING_NOTES: SwingRule[] = [
  (f) =>
    !f.isMelee && f.removeLongRangePenalty && f.rangeBand === 'long' ? 'Marksman: long range penalty negated.' : null,
  (f) =>
    !f.isMelee && f.removeExtremeRangePenalty && f.rangeBand === 'extreme'
      ? 'Marksman: extreme range penalty negated.'
      : null,
  calledShotNote,
  (f) => (f.isAllOutAttack ? 'All-Out Attack: +20 WS, cannot dodge/parry until next turn.' : null),
  (f) => (f.isGuardedAttack ? 'Guarded Attack: -10 WS, may still parry.' : null),
  (f) => (f.isCharge && f.hasBerserkCharge ? 'Berserk Charge: Charge bonus increased to +20 WS.' : null),
  (f) => (f.isAllOutAttack && f.hasHammerBlow ? 'Hammer Blow: +2 damage, +2 penetration on All-Out Attack.' : null),
  (f) => (f.isGangUp ? 'Gang Up: +10 WS (outnumbering target).' : null),
  (f) => (f.frenzied && f.isMelee ? 'Frenzied: +10 WS (melee attack).' : null),
];

/** The talent and combat-action lines a rolled swing prints. */
export function swingNotes(f: SwingFlags): string[] {
  return SWING_NOTES.map((rule) => rule(f)).filter((n): n is string => n !== null);
}

const SHAPE_TEXT: Record<string, (area: ResolvedArea, reach: number) => string> = {
  cone: (area, reach) => `${reach}m cone, ${area.angle}°`,
  blast: (area) => `${area.radius}m blast`,
  beam: (_area, reach) => `${reach}m beam`,
};

/** "Spray: Auto-hit (20m cone, 30° - targets test Agility to avoid)." - the same geometry the map uses. */
export function autoHitNote(slot: Slot, sp: string[], area: ResolvedArea): string {
  const reach = sprayLengthOf(slot);
  const shape = SHAPE_TEXT[area.shape]?.(area, reach) ?? 'area';
  const avoid = area.avoid ? ` - targets test ${AVOID_TEST_LABEL[area.avoid]} to avoid` : '';
  return `${hasWS(sp, 'Spray') ? 'Spray' : 'Area'}: Auto-hit (${shape}${avoid}).`;
}
